import React, { useEffect, useState } from 'react';
import MetricsTable from '@/pages/intelligence/pages/TopCharts/PCGames/MetricsTable';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { lifetimePlayerList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import moment from 'moment';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { getDeviceValuesByPageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import {
  commonPCConsoleDefaultFilterValue,
  handleMetricTableFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { defaultMetrictableApiParams } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { useIntl, history } from 'umi';
import { getTopPCMetricTableCustomValidUrlParams, topchartLifetimePlayerValidCfg, hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

export default function LifetimePlayers() {
  const { formatMessage } = useIntl(); // 中英切换方法
  const pageId = TopchartPageId.LifeTimePlayers;
  const entityType = 'pc';

  const { filterLoading, regionOptions } = useMarketFilterApi({});

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.pcLifetimePlayers?.type,
    rangeTimeType: FilterTimeType.sameMonth,
  });

  const defaultFilterValue = {
    ...commonPCConsoleDefaultFilterValue,
    [filterKeyMap.LIFETIME_DEVICE]: {
      value: getDeviceValuesByPageId(pageId),
    },
  };

  const hasUrlShortLink = hasShortLinkRatio.topPCLifetimePlayers;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: topchartLifetimePlayerValidCfg?.[URL_CHART_TYPE]?.[0],
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopPCMetricTableCustomValidUrlParams(history, topchartLifetimePlayerValidCfg, lifetimePlayerList);
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(dbiPageLogMes.topChartsPCGamesLifetimePlayers);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: [
      filterKeyMap.MY_FILTER,
      filterKeyMap.RANGE_TIME,
      filterKeyMap.REGION,
      filterKeyMap.GENRETOR,
      filterKeyMap.ALL_Games,
      // filterKeyMap.LIFETIME_DEVICE,
      filterKeyMap.ALL_COMPANYS,
      filterKeyMap.RELEASE_TIME,
    ],
    companyFilterParams: {
      entity_type: entityType,
      region_type: 'market',
      regions: ['global'],
      granularity: 'monthly',
      start_date: '',
      end_date: '',
      search_name: '',
      page: 1,
      page_size: 500,
      source: 'newzoo',
      metric: 'lifetime_player',
      data_sources: ['newzoo'],
      source_metric: {
        newzoo: ['lifetime_player'],
      },
    },
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(1, 'months'), moment().subtract(1, 'months')],
      },
    },
    specialParams: {
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      rangeTimeType: FilterTimeType.sameMonth,
      disableIegg: false,
      disableAnnie: true,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType
    },
    iconText: (
      <div>
        <p>{formatMessage({ id: 'dbi_releasedatefilter_info' })}</p>
      </div>
    ),
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handleMetricTableFilterParamsToApi(params, entityType),
    hasUrlShortLink,
  };

  const defaultParams = {
    ...defaultMetrictableApiParams,
    entity_type: entityType,
    order_metric: 'lifetime_player',
    order_source: 'newzoo',
    source_metrics: {
      newzoo: ['lifetime_player'],
    },
    topchart_name: 'Lifetime Players',
    region_type: 'market',
    regions: ['global'],
    date_type: 'monthly',
  };

  return (
    <>
      <MarqueeBanner />
      <MetricsTable
      entityType={entityType}
      pageId={pageId}
      pageLogInfo={dbiPageLogMes.topChartsPCGamesLifetimePlayers}
      visualLoadingHeight={358}
      tableCloumsList={lifetimePlayerList}
      hasRegionFilter={true}
      cardMetricName={formatMessage({ id: 'dbi_lifetime_players' })}
      filterCfg={filterCfg}
      filterLoading={filterLoading || shortLinkLoading}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      defaultParams={defaultParams}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
